import { useMemo } from "react";
import { useGetConfig } from "../api/v1";
import { mapRecordToSelectOptions, sortOptionsByLabel } from "../app/libs/utils";
import { SelectOption } from "../types/select";
import { getLoginResponse } from "../utils/getLoginResponse";

type PaymentTypeConfigKey = "masterPaymentTypeAttributes" | "masterPaymentTypeAttributesWithVat";

type UseGetPaymentTypeOptions = {
  usageType?: string | null;
  isSorted?: boolean;
  configKey?: PaymentTypeConfigKey;
};

type PaymentTypeOptionWithVat = SelectOption & {
  vatInPercentage: number;
  defaultAmount: number;
};

export function useGetPaymentTypeOptions(
  options: UseGetPaymentTypeOptions & { configKey: "masterPaymentTypeAttributesWithVat" },
): {
  paymentTypesOptions: PaymentTypeOptionWithVat[];
  isLoadingConfig: boolean;
};

export function useGetPaymentTypeOptions(
  options: UseGetPaymentTypeOptions & { configKey?: "masterPaymentTypeAttributes" },
): {
  paymentTypesOptions: SelectOption[];
  isLoadingConfig: boolean;
};

export function useGetPaymentTypeOptions(options: UseGetPaymentTypeOptions) {
  const { usageType, isSorted = true, configKey = "masterPaymentTypeAttributes" } = options;

  const { orgId } = getLoginResponse();

  const { config, isLoadingConfig } = useGetConfig({
    params: { key: configKey, orgId, usageType },
    isEnabled: Boolean(orgId),
  });

  const paymentTypesOptions = useMemo(() => {
    if (configKey === "masterPaymentTypeAttributesWithVat") {
      const paymentTypes = config?.[0]?.masterPaymentTypeAttributesWithVat;
      if (!paymentTypes) return [];

      const options = paymentTypes.map(({ key, label, vatInPercentage, defaultAmount }) => ({
        value: key,
        label,
        vatInPercentage,
        defaultAmount: defaultAmount ?? 0,
      }));

      if (isSorted) return sortOptionsByLabel(options);
      return options;
    }

    const paymentTypes = config?.[0]?.masterPaymentTypeAttributes;
    if (!paymentTypes) return [];

    const options = mapRecordToSelectOptions(paymentTypes);

    if (isSorted) return sortOptionsByLabel(options);
    return options;
  }, [config, configKey, isSorted]);

  return { paymentTypesOptions, isLoadingConfig };
}
